import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import './index.css';

// Auto-update del PWA workbox SW.
// Con registerType:'autoUpdate' in vite.config + questa registrazione,
// quando arriva un nuovo SW chiamiamo skipWaiting e ricarichiamo la pagina
// SUBITO: niente piu' "stuck su versione vecchia".
import { registerSW } from 'virtual:pwa-register';
import { isNativeApp } from './lib/platform';

// Quando c'e' una nuova versione, NON ricarichiamo a forza (rischia di
// azzerare il PIN che l'utente sta digitando). Mostriamo un banner
// in fondo allo schermo che chiede tap esplicito.
function showUpdateBanner(activate) {
  if (document.getElementById('nullum-update-banner')) return;
  const div = document.createElement('div');
  div.id = 'nullum-update-banner';
  // Stile coerente con l'app: card arrotondata, verde fosforo (#00ff41) su
  // nero, glow tenue, centrata in basso e rispettosa della safe-area.
  div.style.cssText = [
    'position:fixed','left:50%','transform:translateX(-50%)',
    'bottom:calc(14px + env(safe-area-inset-bottom))','z-index:9999',
    'width:calc(100% - 24px)','max-width:448px','box-sizing:border-box',
    'background:#000','color:#00ff41',
    'border:1px solid rgba(0,255,65,0.5)','border-radius:14px',
    'box-shadow:0 0 16px rgba(0,255,65,0.18)',
    'padding:12px 14px','font-family:ui-monospace,monospace','font-size:14px',
    'display:flex','align-items:center','gap:10px',
  ].join(';');
  div.innerHTML = `
    <span style="flex:1;min-width:0">🔄 Nuova versione disponibile</span>
    <button id="nullum-update-btn" style="background:#00ff41;color:#000;border:0;border-radius:9px;padding:9px 14px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer">Aggiorna</button>
    <button id="nullum-update-dismiss" style="background:transparent;color:#00aa2a;border:1px solid rgba(0,255,65,0.25);border-radius:9px;padding:9px 12px;font-family:inherit;font-size:13px;cursor:pointer">Dopo</button>
  `;
  document.body.appendChild(div);
  document.getElementById('nullum-update-btn').onclick = () => activate(true);
  document.getElementById('nullum-update-dismiss').onclick = () => div.remove();
}
if (isNativeApp()) {
  // GUSCIO NATIVO (Capacitor): NIENTE service worker. Capacitor serve già gli
  // asset in locale dall'APK; un SW qui è solo dannoso — mette in cache il
  // bundle e, poiché i dati dell'app sopravvivono agli update dell'APK, continua
  // a servire la versione VECCHIA del JS ignorando i nuovi APK. Rimuoviamo ogni
  // SW residuo + svuotiamo le cache, così l'app gira SEMPRE sul bundle dell'APK.
  window.__nullumActivateUpdate = () => window.location.reload();
  (async () => {
    try {
      let had = false;
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        had = regs.length > 0 || !!navigator.serviceWorker.controller;
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      if (typeof caches !== 'undefined' && caches?.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
      }
      // Se un SW controllava la pagina, ricarica UNA volta per prendere gli asset
      // freschi dell'APK (guardia anti-loop con sessionStorage).
      if (had && !sessionStorage.getItem('nullum.swPurged')) {
        sessionStorage.setItem('nullum.swPurged', '1');
        window.location.reload();
      }
    } catch { /* best-effort */ }
  })();
} else {
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      showUpdateBanner(updateSW);
    },
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(() => { registration.update().catch(() => {}); }, 5 * 60_000);
        // Controlla aggiornamenti anche quando l'app torna in primo piano: su
        // mobile la PWA viene riaperta di continuo → il banner "Aggiorna" arriva
        // molto prima rispetto al solo intervallo di 5 minuti.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') registration.update().catch(() => {});
        });
      }
    },
    onOfflineReady() {},
  });
  // Esposto per "Verifica aggiornamenti" dal menu / banner: attiva il SW in
  // attesa (gli manda SKIP_WAITING) e ricarica. Se non c'è update, no-op.
  window.__nullumActivateUpdate = () => updateSW(true);
}

// Error boundary "di ultimo livello": meglio di uno schermo nero muto.
// Se l'app crasha durante il render, mostriamo un pannello con il messaggio
// dell'errore + bottoni per pulire cache/SW e ricaricare.
class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null, info: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(err, info) {
    console.error('Root crash', err, info);
    this.setState({ info });
  }
  async hardReset() {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister().catch(() => {})));
      }
      if (typeof caches !== 'undefined' && caches?.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})));
      }
    } catch {}
    const url = new URL(window.location.href);
    url.searchParams.set('_', String(Date.now()));
    window.location.replace(url.toString());
  }
  render() {
    if (!this.state.err) return this.props.children;
    const msg = this.state.err?.message || String(this.state.err);
    const stack = this.state.err?.stack || '';
    return (
      <div style={{
        minHeight: '100dvh',
        background: '#000',
        color: '#22c55e',
        fontFamily: 'ui-monospace, monospace',
        padding: '16px',
        fontSize: '14px',
        lineHeight: 1.4,
      }}>
        <div style={{ color: '#f59e0b', fontSize: '18px', marginBottom: '12px' }}>
          ⚠ NULLUM — errore di avvio
        </div>
        <div style={{ color: '#fff', marginBottom: '8px' }}>
          L'app non è riuscita a partire. Probabilmente un file vecchio in
          cache.
        </div>
        <div style={{
          background: '#111', border: '1px solid #333', padding: '8px',
          marginBottom: '12px', maxHeight: '40vh', overflow: 'auto',
          fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {msg}
          {stack ? '\n\n' + stack.slice(0, 1500) : ''}
        </div>
        <button
          onClick={() => this.hardReset()}
          style={{
            background: '#22c55e', color: '#000', border: 'none',
            padding: '12px 16px', fontFamily: 'inherit', fontSize: '15px',
            width: '100%', marginBottom: '8px', cursor: 'pointer',
          }}
        >
          🔄 Pulisci cache + Service Worker e ricarica
        </button>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: 'transparent', color: '#22c55e',
            border: '1px solid #333', padding: '10px 16px',
            fontFamily: 'inherit', fontSize: '14px', width: '100%',
            cursor: 'pointer',
          }}
        >
          Solo ricarica (senza pulizia)
        </button>
        <div style={{ marginTop: '12px', color: '#666', fontSize: '12px' }}>
          se il problema continua: Impostazioni iPhone → Safari → Avanzate →
          Dati siti web → cerca "nullum.app" → Elimina.
        </div>
      </div>
    );
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>
);
